import { type Lang, type TranslationKey, translate } from '../../i18n';
import { el, icon } from '../../lib/dom';

/**
 * Iskalno polje z eksplicitnim gumbom za brisanje.
 *
 * ⚠️ **Domači `×` pri `type="search"` ni pot, ki bi jo smeli šteti.** Rišeta ga
 * WebKit in Blink, **Firefox ne** — brisanje bi bilo torej odvisno od brskalnika.
 * DS isto drugod rešuje eksplicitno (`.cmb-clear` v comboboxu), zato gumb
 * narišemo sami in domačega skrijemo (`app.css`).
 *
 * **Vsi trije iskalniki so ista komponenta** (Postaje, Iskanje, Zgodovina):
 * ista razporeditev, isti `×`, isto vedenje ob Esc. Trije zasloni s tremi
 * različnimi iskalniki so bili tri različne navade za isto opravilo.
 */

export interface SearchFieldNode {
  root: HTMLElement;
  input: HTMLInputElement;
  /** Prevede ime, ozadnji napis in oznako gumba; uskladi vidnost gumba. */
  update(lang: Lang, labelKey: TranslationKey, placeholderKey: TranslationKey): void;
  /** Uskladi prikazano vrednost s stanjem. Med tipkanjem se ne kliče. */
  setValue(value: string): void;
}

export function createSearchField(id: string, onQuery: (value: string) => void): SearchFieldNode {
  // `id` in `name` nista za nas: brez njiju brskalnik polja ne zna povezati s
  // svojim samodejnim izpolnjevanjem.
  const input = el('input', {
    class: 'input',
    type: 'search',
    id,
    name: id,
    autocomplete: 'off',
  });

  /* Gumb je `.icon-trigger` iz DS: cilj 44px in vedenje ob prehodu sta njegova,
     naša je samo lega (polje zanj ne rezervira prostora). */
  const clear = el(
    'button',
    { class: 'icon-trigger ghost nw-searchfield__clear', type: 'button', hidden: '' },
    [icon('i-x', 16)],
  );

  /** Ena pot za tipkanje in za `×`, da se filtriranje ne podvoji. */
  function apply(value: string): void {
    if (input.value !== value) input.value = value;
    clear.toggleAttribute('hidden', value === '');
    onQuery(value);
  }

  input.addEventListener('input', () => {
    apply(input.value);
  });
  clear.addEventListener('click', () => {
    apply('');
    input.focus();
  });

  /**
   * Esc počisti polje — in dogodek ustavi **samo takrat, kadar je res kaj
   * počistil**. Prazno polje mora Esc prepustiti naprej, sicer bi iskalnik tiho
   * požrl zapiranje modala ali menija nad seboj.
   */
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || input.value === '') return;
    event.preventDefault();
    event.stopPropagation();
    apply('');
  });

  const root = el('div', { class: 'nw-searchfield' }, [input, clear]);

  return {
    root,
    input,

    update(lang, labelKey, placeholderKey) {
      input.setAttribute('aria-label', translate(lang, labelKey));
      input.placeholder = translate(lang, placeholderKey);
      clear.setAttribute('aria-label', translate(lang, 'search.clear'));
      clear.toggleAttribute('hidden', input.value === '');
    },

    setValue(value) {
      if (input.value !== value) input.value = value;
      clear.toggleAttribute('hidden', value === '');
    },
  };
}
